import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import * as Tabs from "@radix-ui/react-tabs";
import Header from "../components/Header";
import Breadcrumb from "../components/Breadcrumb";
import WeekSelector from "../components/WeekSelector";
import BoxScoresTable from "../components/BoxScoresTable";
import WeeklyAwardsTable from "../components/WeeklyAwardsTable";
import PowerRankingsTable from "../components/PowerRankingsTable";
import LuckIndexTable from "../components/LuckIndexTable";
import StandingsTable from "../components/StandingsTable";
import WeeklyNarrative from "../components/WeeklyNarrative";
import LeagueDashboard from "../components/LeagueDashboard";
import LeagueRecordsTab from "../components/LeagueRecordsTab";
import WeeklyRecordsTab from "../components/WeeklyRecordsTab";
import NaughtyList from "../components/NaughtyList";
import SeasonMatrixTable from "../components/SeasonMatrixTable";
import TeamPositionCards from "../components/TeamPositionCards";
import ProviderBetaBanner from "../components/ProviderBetaBanner";
import LeagueTabSkeleton from "../components/LeagueTabSkeleton";
import { safeFetch, fetchWithRetry } from "../utils/api";
import { toast } from "sonner";
import { ArrowLeftRight, History, Share2, Info, Settings } from "lucide-react";
import { setClarityTags, trackClarityEvent } from "../utils/clarity";
import usePageMeta from "../utils/usePageMeta";

// Tabs that use the week selector
const WEEK_TABS = new Set([
    "overview",
    "awards",
    "standings",
    "boxscores",
    "power",
    "luck",
    "naughty",
    "matrix",
    "cards",
    "report",
    "weeklyRecords",
]);

const TAB_LIST = [
    { value: "overview",  label: "Overview" },
    { value: "awards",    label: "Weekly Awards" },
    { value: "weeklyRecords", label: "Weekly records", requires: "history_store" },
    { value: "standings", label: "Standings" },
    { value: "boxscores", label: "Box Scores" },
    { value: "power",     label: "Power Rankings" },
    { value: "luck",      label: "Luck Index" },
    { value: "naughty",   label: "Naughty List", requires: "naughty_list" },
    { value: "cards",     label: "Team Cards", requires: "player_stats" },
    { value: "report",    label: "AI Report", requires: "ai_report" },
    { value: "matrix",    label: "Season Matrix", requires: "player_stats" },
    { value: "records",   label: "Season Records" },
];

const EMPTY_TAB_DATA = {
    awards: null,
    standings: null,
    boxScores: null,
    powerRankings: null,
    prevPowerRankings: null,
    luckIndex: null,
    seasonMatrix: null,
    teamCards: null,
    naughtyList: null,
    ktcValues: null,
};

const LOADING_TAB_DATA = {
    awards: true,
    standings: true,
    boxScores: true,
    powerRankings: true,
    luckIndex: true,
    seasonMatrix: true,
    teamCards: true,
    naughtyList: true,
};

const LEAGUE_TAB_EVENTS = {
    overview: "league_dashboard_viewed",
    awards: "league_tab_awards_viewed",
    standings: "league_tab_standings_viewed",
    boxscores: "league_tab_boxscores_viewed",
    power: "league_tab_power_viewed",
    luck: "league_tab_luck_viewed",
    naughty: "league_tab_naughty_viewed",
    matrix: "league_tab_matrix_viewed",
    cards: "league_tab_cards_viewed",
    report: "league_tab_report_viewed",
};

const BOOTSTRAP_STATES = {
    BOOTSTRAPPING: "bootstrapping",
    READY: "ready",
    PRESEASON: "preseason",
};

const LeaguePage = () => {
    const { provider, leagueYear, leagueId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [leagueData, setLeagueData] = useState(null);
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [currentWeek, setCurrentWeek] = useState(null);
    const [nCompletedWeeks, setNCompletedWeeks] = useState(null);
    const [completedWeeks, setCompletedWeeks] = useState([]);
    const [weekLabels, setWeekLabels] = useState({});
    const [isCustomScoring, setIsCustomScoring] = useState(false);
    const [leagueSettings, setLeagueSettings] = useState(null);
    const [fetchError, setFetchError] = useState(null);
    const [bootstrapState, setBootstrapState] = useState(
        BOOTSTRAP_STATES.BOOTSTRAPPING
    );
    const [tabData, setTabData] = useState(EMPTY_TAB_DATA);
    const [tabLoading, setTabLoading] = useState(LOADING_TAB_DATA);
    const tabFromUrl = new URLSearchParams(location.search).get("tab");
    const [activeTab, setActiveTab] = useState(() =>
        TAB_LIST.some((tab) => tab.value === tabFromUrl) ? tabFromUrl : "overview"
    );
    const capabilities = leagueData?.capabilities || {};
    const dataProfile =
        leagueData?.data_profile || capabilities.data_profile || "full_box_score";
    const isTeamScoreOnly = dataProfile === "team_score_only";
    const supportsPlayerStats = capabilities.supports_player_stats !== false;
    const supportsSimulation = capabilities.supports_simulation !== false;
    const supportsNaughtyList = capabilities.supports_naughty_list !== false;
    const supportsRosterValues = capabilities.supports_roster_values !== false;
    const supportsTradeHub = supportsPlayerStats;
    const supportsHistoryStore = capabilities.supports_history_store !== false;
    const tabList = useMemo(
        () =>
            TAB_LIST
                .filter((tab) => {
                    if (tab.requires === "simulation") return supportsSimulation;
                    if (tab.requires === "player_stats") return supportsPlayerStats;
                    if (tab.requires === "naughty_list") return supportsNaughtyList;
                    if (tab.requires === "history_store") return supportsHistoryStore;
                    if (tab.requires === "ai_report") {
                        return capabilities.supports_ai_report !== false;
                    }
                    return true;
                })
                .map((tab) =>
                    tab.value === "boxscores" && isTeamScoreOnly
                        ? { ...tab, label: "Matchups" }
                        : tab
                ),
        [
            capabilities.supports_ai_report,
            isTeamScoreOnly,
            supportsHistoryStore,
            supportsNaughtyList,
            supportsPlayerStats,
            supportsSimulation,
        ]
    );

    usePageMeta({
        title: leagueData ? `${leagueData.league_name} (${leagueYear})` : `League ${leagueId} — ${leagueYear}`,
        description: leagueData
            ? `Analytics for ${leagueData.league_name} (${leagueYear}) — weekly awards, standings, power rankings, luck index, and playoff odds.`
            : undefined,
        canonical: `/fantasy_stats/league/${provider}/${leagueYear}/${leagueId}/`,
    });

    if (fetchError) {
        throw fetchError;
    }

    useEffect(() => {
        if (!tabList.some((tab) => tab.value === activeTab)) {
            setActiveTab("overview");
        }
    }, [activeTab, tabList]);

    useEffect(() => {
        setClarityTags({
            feature_surface: "league",
            has_league_context: "true",
            provider,
            league_year: String(leagueYear),
            active_tab: activeTab,
        });
    }, [activeTab, leagueYear, provider]);

    useEffect(() => {
        if (!leagueData) return;
        trackClarityEvent(LEAGUE_TAB_EVENTS[activeTab] || "league_tab_viewed");
    }, [activeTab, leagueData]);

    const replaceWeekInUrl = useCallback((week) => {
        const queryParams = new URLSearchParams(window.location.search || location.search);
        queryParams.set("week", week);
        window.history.replaceState(null, "", `${location.pathname}?${queryParams.toString()}`);
    }, [location.pathname, location.search]);

    const replaceTabInUrl = useCallback((tab) => {
        const queryParams = new URLSearchParams(window.location.search || location.search);
        if (tab === "overview") {
            queryParams.delete("tab");
        } else {
            queryParams.set("tab", tab);
        }
        const queryString = queryParams.toString();
        window.history.replaceState(
            null,
            "",
            `${location.pathname}${queryString ? `?${queryString}` : ""}`
        );
    }, [location.pathname, location.search]);

    // Fetch the initial page shell and lightweight week status.
    useEffect(() => {
        setBootstrapState((prev) =>
            prev === BOOTSTRAP_STATES.READY
                ? prev
                : BOOTSTRAP_STATES.BOOTSTRAPPING
        );
        const queryParams = new URLSearchParams(location.search);
        const weekFromUrl = (() => {
            const week = queryParams.get("week");
            return week && !isNaN(parseInt(week)) ? parseInt(week) : null;
        })();

        setLeagueSettings(null);
        safeFetch(
            `/api/league/${provider}/${leagueYear}/${leagueId}/bootstrap/`,
            {},
            false,
            2
        )
            .then((data) => {
                if (data?.redirect) {
                    navigate(data.redirect);
                } else {
                    setLeagueData({
                        id: data.id,
                        league_name: data.league_name,
                        league_year: data.league_year,
                        league_id: data.league_id,
                        is_owner: data.is_owner,
                        discord_webhook_url: data.discord_webhook_url,
                        data_profile: data.data_profile,
                        capabilities: data.capabilities || {},
                        has_league_cup: data.has_league_cup === true,
                    });
                    const completedWeeks =
                        data.n_completed_weeks ??
                        Math.max((data.current_week ?? 1) - 1, 0);
                    const completedWeekOptions = data.completed_weeks?.length
                        ? data.completed_weeks.map(Number).sort((a, b) => a - b)
                        : Array.from(
                              { length: completedWeeks },
                              (_, i) => i + 1
                          );
                    if (completedWeekOptions.length < 1) {
                        setBootstrapState(BOOTSTRAP_STATES.PRESEASON);
                        return;
                    }
                    setCurrentWeek(data.current_week);
                    setNCompletedWeeks(completedWeeks);
                    setCompletedWeeks(completedWeekOptions);
                    setWeekLabels(data.week_labels || {});
                    setIsCustomScoring(data.is_custom_scoring ?? false);
                    const requestedCompletedWeek = weekFromUrl
                        ? completedWeekOptions.filter(
                              (week) => week <= weekFromUrl
                          )
                        : [];
                    const displayWeek = weekFromUrl
                        ? requestedCompletedWeek[
                              requestedCompletedWeek.length - 1
                          ] ?? completedWeekOptions[0]
                        : completedWeekOptions[completedWeekOptions.length - 1];
                    setSelectedWeek(displayWeek);
                    if (weekFromUrl !== displayWeek) {
                        replaceWeekInUrl(displayWeek);
                    }
                    setBootstrapState(BOOTSTRAP_STATES.READY);
                }
            })
            .catch((err) => {
                console.error(
                    "ERROR: /api/league/:provider/:year/:id/bootstrap",
                    leagueYear,
                    leagueId,
                    err
                );
                setFetchError(err);
            });

    }, [provider, location.search, leagueYear, leagueId, navigate, replaceWeekInUrl]);

    // Fetch heavier league settings only when the visible UI needs them.
    useEffect(() => {
        if (!["power", "playoffs"].includes(activeTab) || leagueSettings) return;
        if (activeTab === "power" && !supportsRosterValues) return;
        if (activeTab === "playoffs" && !supportsSimulation) return;

        safeFetch(`/api/league-settings/${provider}/${leagueYear}/${leagueId}/`, {}, false, 2)
            .then((data) => {
                if (data?.redirect) {
                    navigate(data.redirect);
                } else {
                    setLeagueSettings(data);
                }
            })
            .catch((err) => {
                console.error("ERROR: /api/league-settings/:provider/:year/:id", leagueYear, leagueId, err);
                setFetchError(err);
            });
    }, [
        activeTab,
        leagueSettings,
        provider,
        leagueYear,
        leagueId,
        navigate,
        supportsRosterValues,
        supportsSimulation,
    ]);

    // Prefetch all tab data in parallel when week changes.
    // Each table renders as soon as its own request completes.
    useEffect(() => {
        if (
            bootstrapState !== BOOTSTRAP_STATES.READY ||
            !leagueYear ||
            !leagueId ||
            !selectedWeek
        ) return;

        let cancelled = false;
        let redirected = false;
        setTabData({ ...EMPTY_TAB_DATA });
        setTabLoading({ ...LOADING_TAB_DATA });

        const week = selectedWeek;

        const handleRedirect = (data) => {
            if (!data?.redirect && data?.redirect_week === undefined) return false;
            redirected = true;
            if (data.redirect) {
                navigate(data.redirect);
                return true;
            }
            const nextWeek = Number(data.redirect_week);
            if (nextWeek >= 1) {
                setSelectedWeek(nextWeek);
                replaceWeekInUrl(nextWeek);
            } else {
                setBootstrapState(BOOTSTRAP_STATES.PRESEASON);
            }
            return true;
        };

        const setTableLoading = (key, loading) => {
            setTabLoading((prev) => ({ ...prev, [key]: loading }));
        };

        const fetchTable = (key, url) => {
            safeFetch(url, {}, false, 2)
                .then((data) => {
                    if (cancelled || redirected || handleRedirect(data)) return;
                    setTabData((prev) => ({ ...prev, [key]: data }));
                })
                .catch((err) => {
                    if (cancelled || redirected) return;
                    console.error(`Error fetching ${key}:`, err);
                    setFetchError(err);
                })
                .finally(() => {
                    if (!cancelled && !redirected) setTableLoading(key, false);
                });
        };

        fetchTable("awards", `/api/weekly-awards/${provider}/${leagueYear}/${leagueId}/${week}/`);
        fetchTable("standings", `/api/standings/${provider}/${leagueYear}/${leagueId}/${week}/`);
        fetchTable("boxScores", `/api/box-scores/${provider}/${leagueYear}/${leagueId}/${week}/`);
        fetchTable("powerRankings", `/api/power-rankings/${provider}/${leagueYear}/${leagueId}/${week}/`);
        fetchTable("luckIndex", `/api/luck-index/${provider}/${leagueYear}/${leagueId}/${week}/`);
        if (supportsPlayerStats) {
            fetchTable("seasonMatrix", `/api/position-breakdown/${provider}/${leagueYear}/${leagueId}/${week}/?scope=season&view=position`);
            fetchTable("teamCards", `/api/position-cards/${provider}/${leagueYear}/${leagueId}/${week}/`);
        } else {
            setTabData((prev) => ({ ...prev, seasonMatrix: { teams: [] }, teamCards: { teams: [] } }));
            setTableLoading("seasonMatrix", false);
            setTableLoading("teamCards", false);
        }
        if (supportsNaughtyList) {
            fetchTable("naughtyList", `/api/naughty-list/${provider}/${leagueYear}/${leagueId}/${week}/`);
        } else {
            setTabData((prev) => ({ ...prev, naughtyList: [] }));
            setTableLoading("naughtyList", false);
        }

        if (week > 1) {
            safeFetch(`/api/power-rankings/${provider}/${leagueYear}/${leagueId}/${week - 1}/`, {}, false, 2)
                .then((prevPowerRankings) => {
                    if (cancelled || redirected || handleRedirect(prevPowerRankings)) return;
                    setTabData((prev) => ({ ...prev, prevPowerRankings }));
                })
                .catch(() => null);
        }

        return () => { cancelled = true; };
    }, [
        provider,
        leagueYear,
        leagueId,
        selectedWeek,
        bootstrapState,
        navigate,
        replaceWeekInUrl,
        supportsPlayerStats,
        supportsNaughtyList,
    ]);

    // KTC roster values require live roster data, which is expensive for Yahoo.
    // Fetch them only when the Power tab can display them.
    useEffect(() => {
        if (
            activeTab !== "power" ||
            !leagueYear ||
            !leagueId ||
            !selectedWeek ||
            !supportsRosterValues ||
            tabData.ktcValues
        ) return;

        let cancelled = false;
        const week = selectedWeek;

        const handleRedirect = (data) => {
            if (!data?.redirect && data?.redirect_week === undefined) return false;
            if (data.redirect) {
                navigate(data.redirect);
                return true;
            }
            const nextWeek = Number(data.redirect_week);
            if (nextWeek >= 1) {
                setSelectedWeek(nextWeek);
                replaceWeekInUrl(nextWeek);
            } else {
                setBootstrapState(BOOTSTRAP_STATES.PRESEASON);
            }
            return true;
        };

        safeFetch(`/api/ktc-roster-values/${provider}/${leagueYear}/${leagueId}/${week}/`, {}, false, 2)
            .then((ktcValues) => {
                if (cancelled || handleRedirect(ktcValues)) return;
                setTabData((prev) => ({ ...prev, ktcValues }));
            })
            .catch((error) => {
                if (cancelled) return;
                console.error("Error fetching KTC roster values:", error);
                setTabData((prev) => ({
                    ...prev,
                    ktcValues: {
                        data: null,
                        error: "KTC roster values could not be loaded. Try refreshing the page.",
                    },
                }));
            });

        return () => { cancelled = true; };
    }, [
        activeTab,
        provider,
        leagueYear,
        leagueId,
        selectedWeek,
        supportsRosterValues,
        tabData.ktcValues,
        bootstrapState,
        navigate,
        replaceWeekInUrl,
    ]);

    const handleShare = async () => {
        try {
            const response = await fetchWithRetry(
                `/api/league/${leagueData.id}/share/`,
                { method: "POST" },
                1
            );
            const data = await response.json();
            if (response.ok) {
                trackClarityEvent("league_share_link_generated");
                // The API may return a path-only URL (empty FRONTEND_URL); resolve it
                // against the current site so the copied link is shareable.
                const shareUrl = new URL(data.url, window.location.origin).href;
                try {
                    await navigator.clipboard.writeText(shareUrl);
                    toast.success("Share link copied to clipboard!");
                } catch {
                    window.prompt("Copy share link:", shareUrl);
                    toast.success("Share link ready to copy.");
                }
            } else {
                toast.error(data.error || "Failed to generate share link.");
            }
        } catch {
            toast.error("An unexpected error occurred.");
        }
    };

    const handleWeekChange = (newWeek) => {
        const maxWeek = nCompletedWeeks ?? currentWeek;
        const validWeek = Math.max(1, Math.min(newWeek ?? maxWeek, maxWeek));
        setSelectedWeek(validWeek);
        setTabLoading({ ...LOADING_TAB_DATA });
        replaceWeekInUrl(validWeek);
    };

    const handleTabChange = (nextTab) => {
        setActiveTab(nextTab);
        replaceTabInUrl(nextTab);
    };

    const n_simulations = leagueSettings?.regular_season_complete ? 50 : 100;
    const simulationUrl = supportsSimulation
        ? `/fantasy_stats/simulation/${provider}/${leagueYear}/${leagueId}?n_simulations=${n_simulations}`
        : null;
    const historyUrl = `/fantasy_stats/league-history/${provider}/${leagueYear}/${leagueId}/`;
    const cupUrl = `/fantasy_stats/league-cup/${provider}/${leagueYear}/${leagueId}/`;
    const tradeHubUrl = `/fantasy_stats/trade-hub/${provider}/${leagueYear}/${leagueId}/`;
    const settingsUrl = `/fantasy_stats/league/${provider}/${leagueYear}/${leagueId}/settings/`;
    const compactTabShellClass = "mx-auto w-full max-w-5xl";
    const narrowTabShellClass = "mx-auto w-full max-w-4xl";
    const boxScoresTabShellClass = "w-full";
    const compactTabStageClass =
        "rounded-[1.75rem] border border-border/80 bg-white px-4 py-5 shadow-[0_18px_45px_rgba(31,56,41,0.06)] sm:px-6 sm:py-6";

    if (bootstrapState === BOOTSTRAP_STATES.PRESEASON) {
        return (
            <div className="min-h-screen bg-bg flex flex-col">
                <Header />
                <div className="bg-forest border-b-4 border-mustard">
                    <div className="max-w-7xl mx-auto px-6 py-6">
                        <Breadcrumb items={[
                            { label: 'Dashboard', to: '/dashboard' },
                            { label: leagueData ? `${leagueData.league_name} (${leagueYear})` : `League ${leagueId} (${leagueYear})` },
                        ]} />
                        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                            <div>
                                <p className="text-mustard text-xs font-sans font-bold uppercase tracking-widest mb-2">
                                    {leagueYear} Season
                                </p>
                                <h1 className="font-display text-4xl font-black text-white leading-none">
                                    {leagueData?.league_name ?? `League ${leagueId}`}
                                </h1>
                                <p className="text-white/50 font-sans text-sm font-semibold mt-2">
                                    League ID: {leagueData?.league_id ?? leagueId}
                                </p>
                            </div>
                            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:flex-shrink-0">
                                <Link
                                    to={settingsUrl}
                                    className="flex min-h-9 w-full items-center justify-center gap-1.5 px-4 py-2 text-xs font-sans font-bold uppercase tracking-wide text-white/75 border border-white/20 rounded hover:text-white hover:border-white/40 transition-colors sm:w-auto"
                                >
                                    <Settings className="w-3.5 h-3.5" />
                                    Settings
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
                <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-10">
                    <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 border-l-4 border-l-amber-400 rounded-lg px-4 py-3 mb-8 max-w-2xl">
                        <Info className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-amber-800 font-semibold text-sm">Season hasn't started yet</p>
                            <p className="text-amber-700 text-xs mt-0.5">
                                Week 1 hasn't begun. Stats, awards, and power rankings will appear once the season kicks off.
                            </p>
                        </div>
                    </div>
                    <p className="text-xs font-extrabold uppercase tracking-widest text-text-muted mb-3">
                        While you wait…
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                        <Link
                            to={settingsUrl}
                            className="bg-surface border border-border rounded-xl p-5 hover:border-forest/40 hover:shadow-sm transition-all group"
                        >
                            <div className="text-2xl mb-2">⚙️</div>
                            <div className="font-bold text-forest text-sm group-hover:underline">League Settings</div>
                            <div className="text-text-muted text-xs mt-1">Update scoring, rosters, and provider credentials.</div>
                        </Link>
                        <Link
                            to={cupUrl}
                            className="bg-surface border border-border rounded-xl p-5 hover:border-forest/40 hover:shadow-sm transition-all group"
                        >
                            <div className="text-2xl mb-2">🏆</div>
                            <div className="font-bold text-forest text-sm group-hover:underline">League Cup</div>
                            <div className="text-text-muted text-xs mt-1">Set up an in-season tournament before week 1.</div>
                        </Link>
                        {supportsHistoryStore && (
                        <Link
                            to={historyUrl}
                            className="bg-surface border border-border rounded-xl p-5 hover:border-forest/40 hover:shadow-sm transition-all group"
                        >
                            <div className="text-2xl mb-2">📊</div>
                            <div className="font-bold text-forest text-sm group-hover:underline">League History</div>
                            <div className="text-text-muted text-xs mt-1">Browse records and stats from previous seasons.</div>
                        </Link>
                        )}
                        <Link
                            to="/dashboard"
                            className="bg-surface border border-border rounded-xl p-5 hover:border-forest/40 hover:shadow-sm transition-all group"
                        >
                            <div className="text-2xl mb-2">🏠</div>
                            <div className="font-bold text-forest text-sm group-hover:underline">Dashboard</div>
                            <div className="text-text-muted text-xs mt-1">Return to your leagues overview.</div>
                        </Link>
                    </div>
                </main>
            </div>
        );
    }

    if (bootstrapState === BOOTSTRAP_STATES.BOOTSTRAPPING) {
        return (
            <div className="min-h-screen bg-bg">
                <Header />
                <div className="bg-forest border-b-4 border-mustard">
                    <div className="max-w-7xl mx-auto px-6 py-6">
                        <Breadcrumb items={[
                            { label: 'Dashboard', to: '/dashboard' },
                            { label: leagueData ? `${leagueData.league_name} (${leagueYear})` : `League ${leagueId} (${leagueYear})` },
                        ]} />
                        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                            <div>
                                <p className="text-mustard text-xs font-sans font-bold uppercase tracking-widest mb-2">
                                    {leagueYear} Season
                                </p>
                                <h1 className="font-display text-4xl font-black text-white leading-none">
                                    {leagueData
                                        ? leagueData.league_name
                                        : <span className="inline-block w-64 h-9 bg-white/20 rounded animate-pulse" />
                                    }
                                </h1>
                                <p className="text-white/50 font-sans text-sm font-semibold mt-2">
                                    {leagueData
                                        ? `League ID: ${leagueData.league_id}`
                                        : <span className="inline-block w-36 h-4 bg-white/10 rounded animate-pulse" />
                                    }
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
                    <div className="rounded-xl border border-border bg-surface px-5 py-6 text-sm font-semibold text-text-muted shadow-sm">
                        Loading league…
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-bg">
            <Header />
            {/* ── Hero Band ── */}
            <div className="bg-forest border-b-4 border-mustard">
                <div className="max-w-7xl mx-auto px-6 py-6">
                    <Breadcrumb items={[
                        { label: 'Dashboard', to: '/dashboard' },
                        { label: leagueData ? `${leagueData.league_name} (${leagueYear})` : `League ${leagueId} (${leagueYear})` },
                    ]} />
                    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                        <div>
                            <p className="text-mustard text-xs font-sans font-bold uppercase tracking-widest mb-2">
                                {leagueYear} Season
                            </p>
                            <h1 className="font-display text-4xl font-black text-white leading-none">
                                {leagueData
                                    ? leagueData.league_name
                                    : <span className="inline-block w-64 h-9 bg-white/20 rounded animate-pulse" />
                                }
                            </h1>
                            <p className="text-white/50 font-sans text-sm font-semibold mt-2">
                                {leagueData
                                    ? `League ID: ${leagueData.league_id}`
                                    : <span className="inline-block w-36 h-4 bg-white/10 rounded animate-pulse" />
                                }
                            </p>
                        </div>
                        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:flex-shrink-0">
                            {leagueData?.is_owner && (
                                <button
                                    onClick={handleShare}
                                    className="flex min-h-9 w-full items-center justify-center gap-1.5 px-4 py-2 text-xs font-sans font-bold uppercase tracking-wide text-white/75 border border-white/20 rounded hover:text-white hover:border-white/40 transition-colors sm:w-auto"
                                    title="Copy share link"
                                >
                                    <Share2 className="w-3.5 h-3.5" />
                                    Share
                                </button>
                            )}
                            {leagueData?.is_owner && (
                                <Link
                                    to={settingsUrl}
                                    onClick={() => trackClarityEvent("league_settings_opened")}
                                    className="flex min-h-9 w-full items-center justify-center gap-1.5 px-4 py-2 text-xs font-sans font-bold uppercase tracking-wide text-white/75 border border-white/20 rounded hover:text-white hover:border-white/40 transition-colors sm:w-auto"
                                    title="League settings"
                                >
                                    <Settings className="w-3.5 h-3.5" />
                                    Settings
                                </Link>
                            )}
                            {supportsHistoryStore && (
                            <Link
                                to={historyUrl}
                                onClick={() => trackClarityEvent("league_history_opened")}
                                className="flex min-h-9 w-full items-center justify-center gap-1.5 px-4 py-2 text-xs font-sans font-bold uppercase tracking-wide text-white/75 border border-white/20 rounded hover:text-white hover:border-white/40 transition-colors sm:w-auto"
                            >
                                <History className="w-3.5 h-3.5" />
                                History
                            </Link>
                            )}
                            {supportsTradeHub && (
                                <Link
                                    to={tradeHubUrl}
                                    onClick={() => trackClarityEvent("league_trade_hub_opened")}
                                    className="flex min-h-9 w-full items-center justify-center gap-1.5 px-4 py-2 text-xs font-sans font-bold uppercase tracking-wide text-white/75 border border-white/20 rounded hover:text-white hover:border-white/40 transition-colors sm:w-auto"
                                >
                                    <ArrowLeftRight className="w-3.5 h-3.5" />
                                    Trade Hub
                                </Link>
                            )}
                            <Link
                                to={cupUrl}
                                onClick={() => trackClarityEvent("league_cup_opened")}
                                className="flex min-h-9 w-full items-center justify-center px-4 py-2 text-xs font-sans font-bold uppercase tracking-wide text-white/75 border border-white/20 rounded hover:text-white hover:border-white/40 transition-colors sm:w-auto"
                            >
                                League Cup
                            </Link>
                            {supportsSimulation && (
                                <Link
                                    to={simulationUrl}
                                    onClick={() => trackClarityEvent("league_simulation_opened")}
                                    className="flex min-h-9 w-full items-center justify-center px-4 py-2 text-xs font-sans font-bold uppercase tracking-wide bg-mustard text-forest rounded shadow-mustard hover:bg-yellow-500 transition-colors sm:w-auto"
                                >
                                    Simulate Playoff Odds
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Tabs ── */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
                <ProviderBetaBanner provider={provider} leagueId={leagueId} leagueYear={leagueYear} leagueName={leagueData?.league_name} />
                <Tabs.Root value={activeTab} onValueChange={handleTabChange}>

                    {/* Controls row: tab list */}
                    <div className="mb-6 rounded-lg border border-border bg-surface shadow-sm">
                        <div className="flex flex-col gap-3 p-1 sm:flex-row sm:items-center sm:justify-between">
                            <div className="relative min-w-0 flex-1 overflow-visible">
                                <Tabs.List className="flex flex-wrap gap-1">
                                    {tabList.map(({ value, label }) => (
                                        <Tabs.Trigger
                                            key={value}
                                            value={value}
                                            className="
                                                px-3 py-2 text-xs font-sans font-bold uppercase tracking-wide
                                                rounded whitespace-nowrap transition-colors duration-150
                                                text-text-muted hover:text-forest
                                                data-[state=active]:bg-forest data-[state=active]:text-white
                                            "
                                        >
                                            {label}
                                        </Tabs.Trigger>
                                    ))}
                                </Tabs.List>
                            </div>

                            {(WEEK_TABS.has(activeTab) || activeTab === "records") && currentWeek !== null && (
                                <div className="w-full px-1 pb-1 sm:w-40 sm:flex-shrink-0 sm:pb-0">
                                    <WeekSelector
                                        selectedWeek={selectedWeek}
                                        minWeek={1}
                                        maxWeek={nCompletedWeeks ?? currentWeek}
                                        weeks={completedWeeks}
                                        weekLabels={weekLabels}
                                        onWeekChange={handleWeekChange}
                                        disable={activeTab === "records"}
                                        className="week-selector--header"
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <Tabs.Content value="overview" className="outline-none">
                        <LeagueDashboard
                            standings={tabData.standings}
                            awards={tabData.awards}
                            boxScores={tabData.boxScores}
                            week={selectedWeek}
                            loading={tabLoading.standings || tabLoading.awards || tabLoading.boxScores}
                            onOpenTab={handleTabChange}
                            isTeamScoreOnly={isTeamScoreOnly}
                        />
                    </Tabs.Content>

                    <Tabs.Content value="records" className="outline-none">
                        <div className={compactTabStageClass}>
                            <div className={compactTabShellClass}>
                                <LeagueRecordsTab provider={provider} leagueYear={leagueYear} leagueId={leagueId} capabilities={capabilities} />
                            </div>
                        </div>
                    </Tabs.Content>

                    <Tabs.Content value="weeklyRecords" className="outline-none">
                        <WeeklyRecordsTab provider={provider} leagueYear={leagueYear} leagueId={leagueId} week={selectedWeek} />
                    </Tabs.Content>

                    {/* ── Weekly Awards (featured: default tab) ── */}
                    <Tabs.Content value="awards" className="outline-none">
                        <div className={compactTabStageClass}>
                            <div className={compactTabShellClass}>
                                <div className="sr-only">
                                    <h2 className="font-display text-2xl font-bold text-ds-text">
                                        Weekly Awards
                                    </h2>
                                </div>
                                {isCustomScoring && provider === 'sleeper' && (
                                    <div className="mb-4 flex items-center gap-2 bg-blue-50 border border-blue-200 border-l-[3px] border-l-blue-400 rounded px-3 py-2">
                                        <Info className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                                        <p className="text-xs text-blue-700 font-medium">
                                            Projected scores are approximate for custom-scoring leagues.
                                        </p>
                                    </div>
                                )}
                                {tabLoading.awards || !tabData.awards ? (
                                    <LeagueTabSkeleton
                                        variant="awards"
                                        label="Loading weekly awards"
                                    />
                                ) : (
                                    <WeeklyAwardsTable
                                        data={tabData.awards}
                                        week={selectedWeek}
                                        weekLabel={
                                            weekLabels[selectedWeek] ||
                                            weekLabels[String(selectedWeek)]
                                        }
                                        loading={false}
                                        nCompletedWeeks={nCompletedWeeks}
                                    />
                                )}
                            </div>
                        </div>
                    </Tabs.Content>

                    {/* ── Standings ── */}
                    <Tabs.Content value="standings" className="outline-none">
                        <div className={compactTabStageClass}>
                            <div className={compactTabShellClass}>
                                {tabLoading.standings || !tabData.standings ? (
                                    <LeagueTabSkeleton
                                        variant="standings"
                                        label="Loading standings"
                                    />
                                ) : (
                                    <StandingsTable
                                        data={tabData.standings}
                                        week={selectedWeek}
                                        loading={false}
                                    />
                                )}
                            </div>
                        </div>
                    </Tabs.Content>

                    {/* ── Box Scores ── */}
                    <Tabs.Content value="boxscores" className="outline-none">
                        <div className={compactTabStageClass}>
                            <div className={boxScoresTabShellClass}>
                                {isCustomScoring && provider === 'sleeper' && (
                                    <div className="mb-4 flex items-center gap-2 bg-blue-50 border border-blue-200 border-l-[3px] border-l-blue-400 rounded px-3 py-2">
                                        <Info className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                                        <p className="text-xs text-blue-700 font-medium">
                                            Projected scores are approximate for custom-scoring leagues.
                                        </p>
                                    </div>
                                )}
                                {tabLoading.boxScores || !tabData.boxScores ? (
                                    <LeagueTabSkeleton
                                        variant="boxscores"
                                        label={`Loading ${isTeamScoreOnly ? "matchups" : "box scores"}`}
                                    />
                                ) : (
                                    <BoxScoresTable
                                        data={tabData.boxScores}
                                        week={selectedWeek}
                                        loading={false}
                                        title={isTeamScoreOnly ? "Matchups" : "Box Scores"}
                                        fileNamePrefix={isTeamScoreOnly ? "matchups" : "boxscores"}
                                    />
                                )}
                            </div>
                        </div>
                    </Tabs.Content>

                    {/* ── Power Rankings ── */}
                    <Tabs.Content value="power" className="outline-none">
                        <div className={compactTabStageClass}>
                            <div className={compactTabShellClass}>
                                {tabLoading.powerRankings || !tabData.powerRankings ? (
                                    <LeagueTabSkeleton
                                        variant="power"
                                        label="Loading power rankings"
                                    />
                                ) : (
                                    <PowerRankingsTable
                                        data={tabData.powerRankings}
                                        prevData={tabData.prevPowerRankings}
                                        week={selectedWeek}
                                        loading={false}
                                        nCompletedWeeks={nCompletedWeeks}
                                        ktcData={tabData.ktcValues}
                                        ktcLoading={supportsRosterValues && !tabData.ktcValues}
                                        leagueType={leagueSettings?.league_type ?? "redraft"}
                                    />
                                )}
                            </div>
                        </div>
                    </Tabs.Content>

                    {/* ── Luck Index ── */}
                    <Tabs.Content value="luck" className="outline-none">
                        <div className={compactTabStageClass}>
                            <div className={narrowTabShellClass}>
                                {tabLoading.luckIndex || !tabData.luckIndex ? (
                                    <LeagueTabSkeleton
                                        variant="luck"
                                        label="Loading luck index"
                                    />
                                ) : (
                                    <LuckIndexTable
                                        data={tabData.luckIndex}
                                        week={selectedWeek}
                                        loading={false}
                                        nCompletedWeeks={nCompletedWeeks}
                                    />
                                )}
                            </div>
                        </div>
                    </Tabs.Content>

                    {/* ── Naughty List ── */}
                    <Tabs.Content value="naughty" className="outline-none">
                        <div className={compactTabStageClass}>
                            <div className="w-full">
                                {tabLoading.naughtyList || !tabData.naughtyList ? (
                                    <LeagueTabSkeleton
                                        variant="naughty"
                                        label="Loading naughty list"
                                    />
                                ) : (
                                    <NaughtyList
                                        data={tabData.naughtyList}
                                        week={selectedWeek}
                                        loading={false}
                                        nCompletedWeeks={nCompletedWeeks}
                                    />
                                )}
                            </div>
                        </div>
                    </Tabs.Content>

                    <Tabs.Content value="matrix" className="outline-none">
                        <div className={compactTabStageClass}>
                            {tabLoading.seasonMatrix || !tabData.seasonMatrix ? (
                                <LeagueTabSkeleton
                                    variant="matrix"
                                    label="Loading season matrix"
                                />
                            ) : (
                                <SeasonMatrixTable
                                    data={tabData.seasonMatrix}
                                    week={selectedWeek}
                                    loading={false}
                                />
                            )}
                        </div>
                    </Tabs.Content>

                    <Tabs.Content value="cards" className="outline-none">
                        <div className={compactTabStageClass}>
                            {tabLoading.teamCards || !tabData.teamCards ? (
                                <LeagueTabSkeleton
                                    variant="cards"
                                    label="Loading team cards"
                                />
                            ) : (
                                <TeamPositionCards
                                    data={tabData.teamCards}
                                    week={selectedWeek}
                                    loading={false}
                                />
                            )}
                        </div>
                    </Tabs.Content>

                    {/* ── AI Report ── */}
                    {capabilities.supports_ai_report !== false && (
                        <Tabs.Content value="report" className="outline-none">
                            <div className={compactTabStageClass}>
                                <WeeklyNarrative
                                    key={selectedWeek}
                                    provider={provider}
                                    leagueYear={leagueYear}
                                    leagueId={leagueId}
                                    week={selectedWeek}
                                />
                            </div>
                        </Tabs.Content>
                    )}

                </Tabs.Root>

            </main>
        </div>
    );
};

export default LeaguePage;
